/**
 * External Imports
 */
import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';

/**
 * Internal Imports
 */
import EmployeeRoundsReport from './employee/RoundsReport';
import PatientRoundsReport from './patient/RoundsReport';
import EmployeeFrequencyReport from './employee/FrequencyReport';
import CensusReport from './patient/CensusReport';
import ComplianceReport from './patient/ComplianceReport';
import PatientSummaryReport from './patient/PatientSummaryReport';
import EmpComplianceReport from './employee/ComplianceReport';
import {
  setEmployeeRoundsReportSearchQuery,
  setPatientRoundsReportSearchQuery,
  setUserSection,
  clearUserSection
} from '../../redux/actions';

import { useUserRolesRef } from '../../services/UserRoles/userRoles';
import { useDebounceValue } from '../../utils/debouncers';
import EmployeeSummaryReport from './employee/EmployeeSummaryReport';

const Reports: FC<{}> = () => {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState<string>('');

  const dbSearchTerm = useDebounceValue(searchTerm, 275);

  const { '*': view = '' } = useParams();

  const { isPatientRounder, isEmployeeRounder } = useUserRolesRef();

  useEffect(() => {
    dispatch(setUserSection('reports'));
    return () => {
      dispatch(clearUserSection());
    };
  }, []);

  useEffect(() => {
    if (view === 'patient/rounds' || view === 'patient/census') {
      dispatch(setPatientRoundsReportSearchQuery(dbSearchTerm));
    } else if (
      view === 'employee' ||
      view === 'employee/rounds' ||
      view === 'employee/frequency'
    ) {
      dispatch(setEmployeeRoundsReportSearchQuery(dbSearchTerm));
    }
    return () => {
      dispatch(setPatientRoundsReportSearchQuery(''));
      dispatch(setEmployeeRoundsReportSearchQuery(''));
    };
  }, [dbSearchTerm]);

  return (
    <div>
      <Routes>
        {isPatientRounder && (
          <Route path="patient" element={<Outlet />}>
            <Route index element={<ComplianceReport />} />
            <Route path="compliance" element={<ComplianceReport />} />
            <Route
              path="rounds"
              element={
                <PatientRoundsReport
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                />
              }
            />
            <Route
              path="census"
              element={
                <CensusReport
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                />
              }
            />
            <Route path="summary" element={<PatientSummaryReport />} />
          </Route>
        )}
        {isEmployeeRounder && (
          <Route path="employee" element={<Outlet />}>
            <Route path="summary" element={<EmployeeSummaryReport />} />
            <Route path="compliance" element={<EmpComplianceReport />} />
            <Route
              index
              element={
                <EmployeeRoundsReport
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                />
              }
            />
            <Route
              path="rounds"
              element={
                <EmployeeRoundsReport
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                />
              }
            />
            <Route
              path="frequency"
              element={
                <EmployeeFrequencyReport
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                />
              }
            />
          </Route>
        )}
        <Route
          path="*"
          element={
            view.includes('patient') && isPatientRounder ? (
              <Navigate to="patient/compliance" replace />
            ) : (
              <Navigate to="employee/rounds" replace />
            )
          }
        />
      </Routes>
    </div>
  );
};

export default Reports;
